import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge, Card, Group, Progress, Stack, Text } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import DealStatusBadge from '@/components/app/deals/DealStatusBadge';
import NoNds from '@/components/common/NoNds';
import { makeDealUrl } from '@/lib/dealLinks';
import { formatMoney, formatOrgName } from '@/lib/format';
import type { DealOrderType, SearchBargain } from '@/types/deals';
import classes from './DealCard.module.css';
import NumberedId from '@/components/common/NumberedId';

interface DealCardProps {
  readonly deal: SearchBargain;
  /** Сторона текущего пользователя. НЕ задана у агента (посредник) — тогда показываем ОБЕ стороны. */
  readonly orderType?: DealOrderType;
}

// Карточка сделки в списке. Продавец/покупатель видят противоположную сторону относительно вкладки:
// SALE (я продаю) → покупателя; PURCHASE (я покупаю) → продавца. Агент (orderType не задан) — обе стороны.
export default function DealCard({ deal, orderType }: DealCardProps) {
  const { t } = useTranslation();
  const counterparty = orderType === 'SALE' ? deal.buyer : orderType === 'PURCHASE' ? deal.seller : undefined;
  const counterpartyLabel = orderType === 'SALE' ? t('app.deals.summary.buyer') : t('app.deals.summary.seller');
  const dealNumber = deal.specName ?? deal.contractNum;

  return (
    <Card
      withBorder
      padding="md"
      radius="md"
      component={Link}
      to={makeDealUrl(deal.id ?? 0)}
      className={classes.card}
    >
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Text fw={700} lineClamp={1}>
            {dealNumber ? <NumberedId value={dealNumber} /> : `#${deal.id}`}
          </Text>
          <DealStatusBadge status={deal.status} />
        </Group>

        <Group gap="xs" wrap="wrap">
          <Text fz="sm" fw={500}>
            {deal.goodsType ?? '—'}
          </Text>
          {orderType ? (
            counterparty?.name ? (
              <Text fz="sm" c="gray.7">
                · {counterpartyLabel}: {formatOrgName(counterparty.name)}
              </Text>
            ) : null
          ) : (
            // Агент — посредник: показываем обе стороны сделки.
            <>
              {deal.seller?.name ? (
                <Text fz="sm" c="gray.7">
                  · {t('app.deals.summary.seller')}: {formatOrgName(deal.seller.name)}
                </Text>
              ) : null}
              {deal.buyer?.name ? (
                <Text fz="sm" c="gray.7">
                  · {t('app.deals.summary.buyer')}: {formatOrgName(deal.buyer.name)}
                </Text>
              ) : null}
            </>
          )}
        </Group>

        {deal.fromAddressTitle || deal.toAddressTitle ? (
          <Group gap={6} wrap="nowrap" c="gray.7" fz="xs">
            <Text lineClamp={1}>{formatOrgName(deal.fromAddressTitle) ?? deal.fromAddress ?? '—'}</Text>
            <IconArrowRight size={14} style={{ flexShrink: 0 }} />
            <Text lineClamp={1}>{formatOrgName(deal.toAddressTitle) ?? deal.toAddress ?? '—'}</Text>
          </Group>
        ) : null}

        <Group gap="md" wrap="wrap">
          {deal.plannedWeight != null ? (
            <Text fz="sm" c="gray.8">
              {formatMoney(deal.plannedWeight)} {t('app.deals.tons')}
            </Text>
          ) : null}
          {deal.price != null ? (
            <Text fz="sm" c="gray.8">
              {formatMoney(deal.price)} {t('app.deals.rubPerTon')}
              <NoNds />
            </Text>
          ) : null}
        </Group>

        <Group justify="space-between" wrap="nowrap" gap="sm" mt={2}>
          {deal.stage ? (
            <Badge variant="light" color="gray" tt="none" fw={500} radius="sm">
              {deal.stage}
            </Badge>
          ) : (
            <span />
          )}
          {deal.createdDate ? (
            <Text fz="xs" c="gray.6">
              {deal.createdDate}
            </Text>
          ) : null}
        </Group>

        {typeof deal.progress === 'number' && deal.progress > 0 ? (
          <Progress
            value={deal.progress}
            size="sm"
            radius="xl"
            color="brand.6"
            styles={{ root: { backgroundColor: 'var(--mantine-color-brand-1)' } }}
            aria-label={t('app.deals.progress')}
          />
        ) : null}
      </Stack>
    </Card>
  );
}
